'use client'

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from 'react'
import type {
  PromptInputAttachment,
  PromptInputAttachmentsState,
} from '@/components/ai-elements/prompt-input'

type Uploads = Array<PromptInputAttachment>
type UploadsUpdater = (prev: Uploads) => Uploads

const NO_UPLOADS: Uploads = []

/**
 * Chats whose uploads are kept at once. A chat can leave the tab strip
 * without `discard` (cap eviction, brand pruning) and a just-created tab can
 * leave it for a render and come back, so removal can't be tracked per tab;
 * past this bound the least recently changed chat that is NOT retained
 * (open) is dropped. Open chats are never dropped, even past the bound.
 */
export const MAX_CHATS_WITH_UPLOADS = 16

/**
 * The files the user uploaded into each chat's composer, keyed by chat id —
 * the upload twin of the per-chat email and image lattices. Held by
 * `GlobalChatProvider`, so a staged file outlives the chat thread's
 * remounts (tab switch, side flip, split ⇄ full page) and moves with
 * "New chat" (`move`).
 *
 * In memory only: an in-flight upload is a `File` plus a `blob:` preview,
 * neither of which survives a reload.
 */
export type ComposerUploadsStore = {
  subscribe: (listener: () => void) => () => void
  /** Stable reference until that chat's list changes. */
  get: (chatId: string) => Uploads
  /** Set one chat's list (add / remove / clear-on-submit). */
  update: (chatId: string, action: SetStateAction<Uploads>) => void
  /**
   * Land a finished upload wherever its attachment lives NOW. Upload
   * updaters match their attachment by id, and the attachment may have
   * moved to another chat while its upload was in flight — so the updater
   * runs against every chat, and only a chat it changed is replaced.
   */
  settle: (updater: UploadsUpdater) => void
  /** Move every upload staged on `fromChatId` onto the end of `toChatId`. */
  move: (fromChatId: string, toChatId: string) => void
  /**
   * Drop a closed chat's uploads and release their `blob:` previews. An
   * upload still in flight then settles into nothing.
   */
  discard: (chatId: string) => void
}

function isSameList(a: Uploads, b: Uploads): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i])
}

export function createComposerUploadsStore(
  options: {
    /** A retained chat (an open tab) never loses its uploads to the bound. */
    isRetained?: (chatId: string) => boolean
  } = {}
): ComposerUploadsStore {
  const lists = new Map<string, Uploads>()
  const listeners = new Set<() => void>()

  const emit = () => {
    for (const listener of listeners) {
      listener()
    }
  }

  const release = (chatId: string) => {
    for (const file of lists.get(chatId) ?? NO_UPLOADS) {
      if (file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url)
      }
    }
    lists.delete(chatId)
  }

  // An empty list is deleted rather than stored, so `get` falls back to the
  // shared `NO_UPLOADS` and the map never accumulates cleared chats. A write
  // re-inserts the chat, so the map's order is least recently changed first.
  const write = (chatId: string, next: Uploads) => {
    lists.delete(chatId)
    if (next.length === 0) {
      return
    }
    lists.set(chatId, next)
    for (const oldest of [...lists.keys()]) {
      if (lists.size <= MAX_CHATS_WITH_UPLOADS) {
        break
      }
      if (oldest !== chatId && !options.isRetained?.(oldest)) {
        release(oldest)
      }
    }
  }

  const get = (chatId: string) => lists.get(chatId) ?? NO_UPLOADS

  return {
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    get,
    update: (chatId, action) => {
      const prev = get(chatId)
      const next = typeof action === 'function' ? action(prev) : action
      if (isSameList(prev, next)) {
        return
      }
      write(chatId, next)
      emit()
    },
    settle: (updater) => {
      let hasChanged = false
      // A snapshot: `write` re-inserts the chat it changes, which a live
      // iteration would visit again.
      for (const [chatId, prev] of [...lists]) {
        const next = updater(prev)
        if (!isSameList(prev, next)) {
          write(chatId, next)
          hasChanged = true
        }
      }
      if (hasChanged) {
        emit()
      }
    },
    move: (fromChatId, toChatId) => {
      const moving = get(fromChatId)
      if (fromChatId === toChatId || moving.length === 0) {
        return
      }
      lists.delete(fromChatId)
      write(toChatId, [...get(toChatId), ...moving])
      emit()
    },
    discard: (chatId) => {
      if (!lists.has(chatId)) {
        return
      }
      release(chatId)
      emit()
    },
  }
}

/**
 * Bind `chatId`'s slot of the store as a composer's attachment state — pass
 * the result to `PromptInput`'s `attachmentsState`.
 */
export function useComposerUploads(
  store: ComposerUploadsStore,
  chatId: string
): PromptInputAttachmentsState {
  const files = useSyncExternalStore(
    store.subscribe,
    () => store.get(chatId),
    () => NO_UPLOADS
  )
  const setFiles = useCallback<Dispatch<SetStateAction<Uploads>>>(
    (action) => store.update(chatId, action),
    [store, chatId]
  )
  return useMemo(
    () => ({ files, setFiles, settleUpload: store.settle }),
    [files, setFiles, store]
  )
}
